/**
 * Google Analytics Service
 * Handles all Google Analytics initialization and event tracking.
 * On Capacitor (iOS/Android), events are sent via Firebase Analytics.
 * On web, events are sent via Google Analytics (gtag).
 */

import { getConfig } from '../config';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { Capacitor } from '@capacitor/core';

const isCapacitor = Capacitor.isNativePlatform();

/**
 * Apply Google Consent Mode v2 based on user cookie preferences.
 * Must be called before the GA script loads (sets defaults) and again
 * after the user updates their consent (updates the running instance).
 *
 * @param {Object} prefs - Cookie consent preferences
 * @param {boolean} prefs.analytics  - Analytics/measurement cookies
 * @param {boolean} prefs.marketing  - Ad/marketing cookies
 * @param {boolean} prefs.preferences - Functionality/personalization cookies
 */
export const applyConsentMode = (prefs = {}) => {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function() { window.dataLayer.push(arguments); };
  }

  window.gtag('consent', 'update', {
    analytics_storage:      prefs.analytics   ? 'granted' : 'denied',
    ad_storage:             prefs.marketing   ? 'granted' : 'denied',
    ad_user_data:           prefs.marketing   ? 'granted' : 'denied',
    ad_personalization:     prefs.marketing   ? 'granted' : 'denied',
    functionality_storage:  prefs.preferences ? 'granted' : 'denied',
    personalization_storage: prefs.preferences ? 'granted' : 'denied',
  });
};

/**
 * Initialize Google Analytics with Consent Mode v2.
 * Sets consent defaults to 'denied', then applies saved preferences if any.
 * The GA script is always loaded so that consent signals are sent to Google
 * even in denied mode (required for modelled conversions).
 */
export const initializeGA = () => {
  // Check if GA is already initialized
  if (window.gtag) {
    return;
  }

  try {
    // Set up dataLayer and gtag before the script loads
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() { window.dataLayer.push(arguments); };

    // Default: deny all non-essential consent until the user decides
    window.gtag('consent', 'default', {
      analytics_storage:       'denied',
      ad_storage:              'denied',
      ad_user_data:            'denied',
      ad_personalization:      'denied',
      functionality_storage:   'denied',
      personalization_storage: 'denied',
      wait_for_update: 500,
    });

    // Apply any previously saved consent immediately
    try {
      const saved = JSON.parse(localStorage.getItem('kynaix_cookie_consent') || 'null');
      if (saved?.decided) {
        applyConsentMode(saved);
      }
    } catch {
      // ignore parse errors
    }

    // Load the GA script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${getConfig('GA_MEASUREMENT_ID')}`;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    // Set initial timestamp and configure GA
    window.gtag('js', new Date());
    window.gtag('config', getConfig('GA_MEASUREMENT_ID'), {
      send_page_view: true
    });

  } catch (error) {
    console.error('Error initializing Google Analytics:', error);
  }
};

/**
 * Track a page view
 * @param {string} path - The page path to track
 * @param {string} title - The page title (optional)
 */
export const trackPageView = (path, title = '') => {
  if (isCapacitor) {
    FirebaseAnalytics.logEvent({
      name: 'screen_view',
      params: { screen_name: path, screen_class: title || path },
    }).catch(() => {});
    return;
  }

  if (!window.gtag) return;
  try {
    window.gtag('event', 'page_view', { page_path: path, page_title: title });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

/**
 * Track a custom event
 * @param {string} eventName - The name of the event
 * @param {Object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (isCapacitor) {
    // Firebase event names must be alphanumeric + underscore, max 40 chars
    const safeName = eventName.replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 40);
    FirebaseAnalytics.logEvent({ name: safeName, params: eventParams }).catch(() => {});
    return;
  }

  if (!window.gtag) return;
  try {
    window.gtag('event', eventName, eventParams);
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Track a user interaction
 * @param {string} category - The event category (e.g., 'button', 'link', 'form')
 * @param {string} action - The action taken (e.g., 'click', 'submit', 'view')
 * @param {string} label - Additional label for the event (optional)
 */
export const trackInteraction = (category, action, label = '') => {
  trackEvent('interaction', {
    event_category: category,
    event_action: action,
    event_label: label
  });
};

/**
 * Track tab click/switch events
 * @param {string} tabName - Name of the tab clicked
 * @param {string} pageContext - Where the tab is located (e.g., 'profile', 'tournaments')
 * @param {Object} additionalParams - Any additional context
 */
export const trackTabClick = (tabName, pageContext = '', additionalParams = {}) => {
  if (!tabName) {
    console.warn('Tab name is required for tracking');
    return;
  }

  trackEvent('tab_click', {
    tab_name: tabName,
    page_context: pageContext,
    page_location: window.location.pathname,
    ...additionalParams
  });
};

/**
 * Track button click events
 * @param {string} buttonName - Name/identifier of the button clicked
 * @param {string} pageContext - Where the button is located (optional)
 * @param {Object} additionalParams - Any additional context
 */
export const trackButtonClick = (buttonName, pageContext = '', additionalParams = {}) => {
  if (!buttonName) {
    console.warn('Button name is required for tracking');
    return;
  }

  trackEvent('button_click', {
    button_name: buttonName,
    page_context: pageContext,
    page_location: window.location.pathname,
    ...additionalParams
  });
};

/**
 * Set user ID for authenticated users (GDPR/Privacy compliant)
 * Call this when a user logs in to track them across sessions
 * @param {string} userId - The user ID (use a hashed or anonymized ID, NOT email or PII)
 */
export const setUserId = (userId) => {
  if (!userId) return;

  if (isCapacitor) {
    FirebaseAnalytics.setUserId({ userId }).catch(() => {});
    return;
  }

  if (!window.gtag) return;
  try {
    window.gtag('config', getConfig('GA_MEASUREMENT_ID'), { user_id: userId });
  } catch (error) {
    console.error('Error setting user ID:', error);
  }
};

/**
 * Clear user ID (call on logout)
 */
export const clearUserId = () => {
  if (isCapacitor) {
    FirebaseAnalytics.setUserId({ userId: null }).catch(() => {});
    return;
  }

  if (!window.gtag) return;
  try {
    window.gtag('config', getConfig('GA_MEASUREMENT_ID'), { user_id: null });
  } catch (error) {
    console.error('Error clearing user ID:', error);
  }
};

/**
 * Set user properties (for authenticated users)
 * @param {Object} userProperties - Additional user properties (e.g., subscription_type, user_role)
 */
export const setUserProperties = (userProperties = {}) => {
  if (!window.gtag) {
    console.warn('Google Analytics not initialized. Call initializeGA() first.');
    return;
  }

  try {
    window.gtag('set', 'user_properties', userProperties);
  } catch (error) {
    console.error('Error setting user properties:', error);
  }
};

/**
 * Set user demographics (optional - for better insights)
 * @param {Object} demographics - User demographics (age_group, gender, interests)
 */
export const setUserDemographics = (demographics = {}) => {
  if (!window.gtag) {
    console.warn('Google Analytics not initialized. Call initializeGA() first.');
    return;
  }

  try {
    const allowedDemographics = {};

    // Only set allowed demographic fields
    if (demographics.age_group) allowedDemographics.age_group = demographics.age_group;
    if (demographics.gender) allowedDemographics.gender = demographics.gender;
    if (demographics.interests) allowedDemographics.interests = demographics.interests;

    window.gtag('set', 'user_properties', allowedDemographics);
  } catch (error) {
    console.error('Error setting user demographics:', error);
  }
};

export default {
  initializeGA,
  trackPageView,
  trackEvent,
  trackInteraction,
  trackTabClick,
  trackButtonClick,
  setUserId,
  clearUserId,
  setUserProperties,
  setUserDemographics
};
